import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
} from 'react';
import PropTypes from 'prop-types';

import { SidePanelService } from '@ohif/core';

interface DefaultState {
  panelName: string | null;
  open: boolean;
}

const DEFAULT_STATE: DefaultState = {
  panelName: null,
  open: false,
};

export const SidePanelContext = createContext(DEFAULT_STATE);
export const SidePanelDispatchContext = createContext(null);

export function SidePanelProvider({ children, service }) {
  const sidePanelReducer = (state: DefaultState, action) => {
    switch (action.type) {
      case 'CLOSE_PANEL': {
        return {
          ...state,
          open: false,
        };
      }
      case 'OPEN_PANEL': {
        return {
          ...state,
          open: true,
        };
      }
      case 'SET_PANEL_DATA': {
        const { panelName, open } = action.payload;

        return {
          ...state,
          panelName,
          open,
        };
      }

      default:
        return action.payload;
    }
  };

  const [sidePanelState, dispatch] = useReducer(
    sidePanelReducer,
    DEFAULT_STATE
  );

  const getState = useCallback(() => {
    return sidePanelState;
  }, [sidePanelState]);

  const setPanelData = useCallback(
    ({ panelName, open }) =>
      dispatch({
        type: 'SET_PANEL_DATA',
        payload: { panelName, open },
      }),
    [dispatch]
  );

  const closePanel = useCallback(
    () =>
      dispatch({
        type: 'CLOSE_PANEL',
        payload: {},
      }),
    [dispatch]
  );

  const openPanel = useCallback(
    () =>
      dispatch({
        type: 'OPEN_PANEL',
        payload: {},
      }),
    [dispatch]
  );

  /**
   * Sets the implementation of SidePanelService that can be used by extensions.
   *
   * @returns void
   */
  useEffect(() => {
    if (service) {
      service.setServiceImplementation({
        getState,
        setPanelData,
        closePanel,
        openPanel,
      });
    }
  }, [setPanelData, closePanel, getState, openPanel]);

  const api = {
    getState,
    setPanelData: (props) => service.setPanelData([props]),
    closePanel: () => service.closePanel(),
    openPanel: () => service.openPanel(),
  };

  return (
    <SidePanelContext.Provider value={sidePanelState}>
      <SidePanelDispatchContext.Provider value={api}>
        {children}
      </SidePanelDispatchContext.Provider>
    </SidePanelContext.Provider>
  );
}

SidePanelProvider.propTypes = {
  children: PropTypes.any,
  service: PropTypes.instanceOf(SidePanelService).isRequired,
};

export const useSidePanelContext = () => useContext(SidePanelContext);
export const useSidePanelDispatch = () => useContext(SidePanelDispatchContext);
export const useSidePanel = () => [
  useContext(SidePanelContext),
  useContext(SidePanelDispatchContext),
];
